import { useCallback } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { getPreviousRoute } from './routes';

export const useNavigationBack = () => {
  const match = useRouteMatch();

  const onLandingPageBack = useLandingPageBackNavigation(match);
  const onStandardPageBack = useStandardPageBackNavigation();

  // Use browser history for detail pages (experiences, collections, events, checkout flows).
  // The `/e/` check covers experience detail routes (e.g. `/e/:experienceId`) — without it, back
  // from an experience opened via the schedule view falls through to the landing-page route map
  // and lands on `/` instead of returning to `/calendar`. Using `/e/` (not just `/e`) avoids
  // false-matching `/event/...` which has no immediate trailing slash after the `e`.
  if (
    match?.path?.includes('/collection') ||
    match?.path?.includes('/event') ||
    match?.path?.includes('/e/') ||
    match?.path?.includes('/addons') ||
    match?.path?.includes('/participant') ||
    match?.path?.includes('/checkout') ||
    match?.path?.includes('/booking') ||
    match?.path?.includes('/waitlist')
  ) {
    return onStandardPageBack;
  }

  // Landing pages (/, /search, /landing/:product) use static route mapping
  return onLandingPageBack;
};

const useLandingPageBackNavigation = (
  match: ReturnType<typeof useRouteMatch>,
) => {
  const history = useHistory();
  const location = useLocation();
  const newPath = getPreviousRoute(match);

  const onBack = useCallback(() => {
    history.replace({
      pathname: newPath ?? '/',
      search: location.search,
    });
  }, [newPath, history, location.search]);

  return onBack;
};

const useStandardPageBackNavigation = () => {
  const history = useHistory();

  const onBack = useCallback(() => {
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push('/');
    }
  }, [history]);

  return onBack;
};
